import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useWebServer } from "../../../../../providers";

// The field registry (GET /organization/report-fields) and each field's
// values (GET /organization/report-fields/:key/values), fetched once and
// shared: the registry once per session, values once per organization and
// field. A failed fetch is forgotten so the next use tries again.
const cache = new Map();

const cached = (key, load) => {
  if (!cache.has(key)) {
    cache.set(
      key,
      load().catch((err) => {
        cache.delete(key);
        throw err;
      })
    );
  }
  return cache.get(key);
};

export const clearReportFieldCache = () => cache.clear();

/**
 * Every registry field: {fields, failed, retry}. `fields` is [] until they
 * load; `failed` says the last try did not, and `retry` tries again.
 */
export const useAllReportFields = () => {
  const { sendOrganizationRequest } = useWebServer();
  const [state, setState] = useState(() => ({ fields: [], failed: false }));
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let current = true;
    cached("fields", () =>
      sendOrganizationRequest("get")({ url: "/organization/report-fields" }).then((res) => res.data?.fields ?? [])
    )
      .then((fields) => current && setState({ fields, failed: false }))
      .catch(() => current && setState({ fields: [], failed: true }));
    return () => {
      current = false;
    };
    // the provider rebuilds sendOrganizationRequest on render; this loads once
    // and again on retry
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);
  const retry = useCallback(() => {
    setState((s) => ({ ...s, failed: false }));
    setAttempt((n) => n + 1);
  }, []);
  return useMemo(() => ({ ...state, retry }), [state, retry]);
};

/** A field's values, {values: [{value, label}], loading}; none for typed fields. */
export const useFieldValues = (key, enabled = true) => {
  const { sendOrganizationRequest } = useWebServer();
  const organization = useSelector((state) => state.user?.organization);
  const [state, setState] = useState({ values: [], loading: false });
  useEffect(() => {
    if (!key || !enabled) return undefined;
    let current = true;
    setState((s) => ({ ...s, loading: true }));
    cached(`values:${organization}:${key}`, () =>
      sendOrganizationRequest("get")({ url: `/organization/report-fields/${encodeURIComponent(key)}/values` }).then(
        (res) => res.data?.values ?? []
      )
    )
      .then((values) => current && setState({ values, loading: false }))
      .catch(() => current && setState({ values: [], loading: false }));
    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, organization]);
  return state;
};

/** The day of the organization's first specimen (YYYY-MM-DD), for All time. */
export const useFirstReportDate = () => {
  const { sendOrganizationRequest } = useWebServer();
  const organization = useSelector((state) => state.user?.organization);
  return () =>
    cached(`first:${organization}`, () =>
      sendOrganizationRequest("get")({ url: "/organization/reports/first-date" }).then((res) => res.data?.firstDate ?? null)
    );
};
